import { useEffect, useMemo, useState } from 'react'
import type { ColumnSchema, DataTableTheme, Transaction } from '../types'
import { formatAmount, formatExpenseDocumentType, formatThaiDate, BANK_LOGOS, THAI_BANKS, normalizeThaiBankKey } from '../utils/formatters'
import { ThaiDatetimeText } from './ThaiDatetimeText'
import { StatusBadge } from './StatusBadge'
import { ImageViewer } from './ImageViewer'
import { getDisplayText, getDisplayValue, formatReferenceNo, isReferenceField } from '../utils/display'
import { resolveSlipMatchedAmount } from '../utils/incomeDerived'
import { resolveTransactionImageCandidates } from '../utils/transactionImages'
import { MissingImagePlaceholder } from './MissingImagePlaceholder'
import { AeraImage } from './DataTableImage'
import { AeraButton } from '@aera/ui'

export interface CellRendererProps {
  col: ColumnSchema
  value: unknown
  record: Transaction
  searchQuery?: string | null
  themeOverrides?: Partial<DataTableTheme>
  workspaceId?: string
  variant?: 'table' | 'drawer'
}

function highlightText(text: string, query?: string | null) {
  const normalizedQuery = query?.trim()
  if (!normalizedQuery) return text

  const lowerText = text.toLocaleLowerCase()
  const lowerQuery = normalizedQuery.toLocaleLowerCase()
  const parts: React.ReactNode[] = []
  let start = 0
  let index = lowerText.indexOf(lowerQuery, start)

  while (index !== -1) {
    if (index > start) {
      parts.push(text.slice(start, index))
    }

    const match = text.slice(index, index + normalizedQuery.length)
    parts.push(
      <mark key={`${index}-${match}`} className="bg-(--aera-dt-highlight-surface) text-inherit rounded-sm px-0.5">
        {match}
      </mark>
    )

    start = index + normalizedQuery.length
    index = lowerText.indexOf(lowerQuery, start)
  }

  if (start < text.length) {
    parts.push(text.slice(start))
  }

  return parts.length > 0 ? parts : text
}

function TransactionImageCell({
  col,
  value,
  record,
  themeOverrides,
  workspaceId: workspaceIdProp,
}: CellRendererProps) {
  const [viewerOpen, setViewerOpen] = useState(false)
  const [candidateIndex, setCandidateIndex] = useState(0)
  const [imageLoadFailed, setImageLoadFailed] = useState(false)
  const displayValue = getDisplayValue(value)

  const fromQuery = typeof window !== 'undefined'
    ? new URL(window.location.href).searchParams.get('workspace_id') ??
      new URL(window.location.href).searchParams.get('workspaceId')
    : null
  const fromRecord = record?.workspace_id ?? record?.workspaceId ?? record?.workspace
  const resolvedWorkspaceId = (workspaceIdProp ?? fromRecord ?? fromQuery ?? '').toString().trim()

  const imageCandidates = useMemo(
    () =>
      resolveTransactionImageCandidates({
        record: record as Record<string, unknown>,
        workspaceId: resolvedWorkspaceId,
        columnValue: displayValue,
      }),
    [displayValue, record, resolvedWorkspaceId],
  )
  const candidateKey = imageCandidates.join('\0')

  useEffect(() => {
    setCandidateIndex(0)
    setImageLoadFailed(false)
  }, [candidateKey, record?.record_id, col.id])

  const imgUrl = imageCandidates[candidateIndex] ?? null

  if (!imgUrl || imageLoadFailed) {
    const docType = String(
      getDisplayValue(record?.document_type) ?? record?.document_type ?? "",
    )
      .trim()
      .toLowerCase()
    const isInvoice =
      docType === "invoice" ||
      docType === "ใบแจ้งหนี้" ||
      /invoice|ใบแจ้งหนี้|ใบกำกับ/i.test(docType)
    const fallbackSrc = isInvoice ? "/img/invoice.svg" : "/img/withdraw.svg"
    const isExpenseDoc =
      Boolean(String(record?.document_type ?? "").trim()) ||
      /expense/i.test(
        String(record?.worker_id ?? record?.workerId ?? ""),
      )
    return (
      <div className="flex items-center justify-start w-full h-full">
        <div className="h-9 w-9 overflow-hidden rounded-md bg-aera-offwhite">
          {isExpenseDoc ? (
            // eslint-disable-next-line @next/next/no-img-element
            <img
              src={fallbackSrc}
              alt=""
              className="h-full w-full object-contain p-1"
            />
          ) : (
            <MissingImagePlaceholder iconClassName="h-3.5 w-3.5" />
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="flex items-center justify-start w-full h-full" onClick={(e) => e.stopPropagation()}>
      <AeraButton
        variant="ghost"
        size="xs"
        type="button"
        className="w-9 h-9 p-0! rounded-md overflow-hidden bg-aera-lightgray hover:bg-aera-lightgray/50 active:scale-95 group/img"
        onClick={(e) => {
          e.stopPropagation()
          setViewerOpen(true)
        }}
      >
        <AeraImage
          src={imgUrl}
          alt="Thumbnail"
          width={36}
          height={36}
          loading="lazy"
          decoding="async"
          className="h-full w-full object-cover transition-transform group-hover/img:scale-110"
          onError={() => {
            if (candidateIndex + 1 < imageCandidates.length) {
              setCandidateIndex((index) => index + 1)
              return
            }
            setImageLoadFailed(true)
          }}
        />
      </AeraButton>
      <ImageViewer
        open={viewerOpen}
        onOpenChange={setViewerOpen}
        imageUrl={imgUrl}
        caption={`${getDisplayText(record.record_id)} · ${col.header}`}
        themeOverrides={themeOverrides}
      />
    </div>
  )
}

export function CellRenderer({ col, value, record, searchQuery, themeOverrides, workspaceId: workspaceIdProp, variant = 'table' }: CellRendererProps) {
  const displayValue = getDisplayValue(value)
  const displayText = getDisplayText(value)

  if (displayValue == null && col.type !== 'image') {
    return <span className="text-aera-lightslate">—</span>
  }

  switch (col.type) {
    case 'amount':
      if (col.id === 'slip_matched_amount' || col.id === 'allocated_amount') {
        const matchedAmount = resolveSlipMatchedAmount(record)
        if (matchedAmount == null) {
          return <span className="text-aera-lightslate">—</span>
        }
        return (
          <span className="block w-full font-bold leading-5 text-aera-charcoal text-right tabular-nums tracking-tight">
            {formatAmount(matchedAmount)}
          </span>
        )
      }
      if (col.id === 'unallocated_amount') {
        const pending = Number(getDisplayValue(record.unallocated_amount))
        const value = Number.isFinite(pending) ? pending : 0
        return (
          <span className="block w-full font-bold leading-5 text-aera-green text-right tabular-nums tracking-tight">
            {formatAmount(value)}
          </span>
        )
      }
      return <span className="block w-full font-bold leading-5 text-aera-charcoal text-right tabular-nums tracking-tight">{formatAmount(displayValue as number)}</span>
    
    case 'datetime':
      return <ThaiDatetimeText value={displayValue as string} />
    
    case 'date':
      return <span className="font-medium leading-5 text-aera-gray tabular-nums whitespace-nowrap">{formatThaiDate(displayValue as string)}</span>
    
    case 'status':
      return <StatusBadge status={displayText} />
    
    case 'bank': {
      const bankCodeRaw = displayValue
      const normalizedKey = normalizeThaiBankKey(bankCodeRaw) ?? getDisplayText(bankCodeRaw)
      const logoUrl = BANK_LOGOS[normalizedKey]
      const bankLabel = (() => {
        const key = normalizeThaiBankKey(bankCodeRaw)
        if (!key) return getDisplayText(bankCodeRaw)
        const bank = THAI_BANKS[key]
        return bank?.symbol ?? bank?.name ?? bank?.nameLong ?? getDisplayText(bankCodeRaw)
      })()

        return (
        <div className="grid grid-cols-[20px_auto] items-center gap-2.5">
          <div className="w-5 h-5 rounded-full bg-aera-lightgray flex items-center justify-center overflow-hidden shrink-0">
            {logoUrl ? (
              <AeraImage src={logoUrl} alt={bankLabel} width={20} height={20} className="w-full h-full object-cover" />
            ) : (
              <span className="text-[8px] font-bold text-aera-lightslate">{bankLabel.substring(0, 2)}</span>
            )}
          </div>
          <span className="font-semibold text-aera-gray truncate">{bankLabel}</span>
        </div>
      )
    }
    
    case 'image':
      return (
        <TransactionImageCell
          col={col}
          value={value}
          record={record}
          themeOverrides={themeOverrides}
          workspaceId={workspaceIdProp}
        />
      )
    
    case 'tel':
      return (
        <a 
          href={`tel:${displayText}`}
          className="block truncate font-medium leading-5 text-aera-gray transition-colors hover:text-aera-charcoal hover:underline cursor-pointer" 
          onClick={(e) => e.stopPropagation()}
        >
          {highlightText(displayText, searchQuery)}
        </a>
      )
    
    case 'id':
      return (
        <span className="inline-flex items-center text-[10px] font-bold leading-4 text-aera-lightslate bg-aera-lightgray px-1.5 py-0.5 rounded-md">
          {highlightText(displayText, searchQuery)}
        </span>
      )
    
    case 'text':
    default: {
      const referenceField = isReferenceField(col.id)
      const tableReference = referenceField && variant === 'table'
      const labeled =
        col.id === 'document_type'
          ? formatExpenseDocumentType(displayText)
          : displayText
      const textForDisplay = tableReference ? formatReferenceNo(labeled) : labeled
      return (
        <span
          className={
            tableReference
              ? 'block font-medium leading-5 text-aera-gray tabular-nums whitespace-nowrap'
              : referenceField
                ? 'block max-w-full whitespace-pre-wrap break-all font-medium leading-6 text-aera-charcoal tabular-nums'
                : 'block max-w-full truncate font-medium leading-5 text-aera-gray'
          }
        >
          {highlightText(textForDisplay, searchQuery)}
        </span>
      )
    }
  }
}
